import type { ReactNode } from 'react';
import {
  CalendarIcon,
  CandleIcon,
  ChevronIcon,
  ChurchIcon,
  ClockIcon,
  CrossMark,
  Ornament,
  PinIcon,
  QuestionIcon,
} from './icons.tsx';

/** An icon that hints what a suggested question is about. */
export function suggestionIcon(question: string): ReactNode {
  const q = question.toLocaleLowerCase('es');
  if (/ahora|en la calle/.test(q)) return <CandleIcon />;
  if (/iglesia|templo|parroquia|capilla|basílica/.test(q)) return <ChurchIcon />;
  if (/a qué hora|horario/.test(q)) return <ClockIcon />;
  if (/pasa por|recorrido|calle|plaza|campana|estoy en/.test(q)) return <PinIcon />;
  if (/salen|empieza|hoy|mañana|domingo|lunes|martes|miércoles|jueves|viernes|sábado/.test(q)) {
    return <CalendarIcon />;
  }
  return <QuestionIcon />;
}

/** A tappable card: warm white, fine border, soft hover and press. */
export const cardClass =
  'group flex w-full items-center gap-3 rounded-xl border border-purple/15 bg-paper px-3 py-2.5 ' +
  'text-left text-ink shadow-[0_1px_0_rgb(72_32_82/0.04)] transition duration-150 ' +
  'hover:border-purple/35 hover:bg-white active:scale-[0.99] active:bg-cream-200/60 ' +
  'dark:border-cream/12 dark:bg-night-800 dark:text-cream dark:hover:border-gold/50 ' +
  'dark:hover:bg-night-700 dark:active:bg-night-700';

export function Welcome({
  suggestions,
  onPick,
}: {
  suggestions: string[];
  onPick: (s: string) => void;
}) {
  return (
    <section className="animate-rise px-1 pt-2">
      <h2 className="font-serif text-[2.1rem] leading-[1.05] font-semibold text-purple dark:text-cream">
        La Semana Santa, a tu alcance
      </h2>
      <p className="mt-2 text-base leading-snug text-ink-soft dark:text-cream/75">
        Consulta horarios, recorridos y hermandades. Pregunta lo que quieras.
      </p>
      <Ornament className="mt-3" />

      <ul className="mt-3.5 space-y-2" aria-label="Preguntas sugeridas">
        {suggestions.map((s) => (
          <li key={s}>
            <button type="button" onClick={() => onPick(s)} className={cardClass}>
              <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-purple/[0.07] text-purple dark:bg-gold/10 dark:text-gold-300">
                {suggestionIcon(s)}
              </span>
              <span className="flex-1 text-base leading-snug">{s}</span>
              <span className="text-gold-700 transition-transform duration-150 group-hover:translate-x-0.5 dark:text-gold">
                <ChevronIcon />
              </span>
            </button>
          </li>
        ))}
      </ul>

      <a
        href="/reto/nuevo"
        className="group mt-4 flex items-center gap-3 rounded-xl border border-gold/60 bg-cream-200 px-4 py-3 text-ink transition duration-150 hover:border-gold active:scale-[0.99] dark:border-gold/30 dark:bg-night-800/70 dark:text-cream dark:hover:border-gold/60"
      >
        <span className="text-gold-700 dark:text-gold">
          <CrossMark size={26} />
        </span>
        <span className="min-w-0 flex-1">
          <strong className="block font-serif text-xl leading-tight font-semibold">
            ¿Cuánto sabes de Semana Santa?
          </strong>
          <span className="mt-0.5 block text-sm leading-snug text-ink-soft dark:text-cream/70">
            Acepta el reto cofrade y desafía a tus amigos.
          </span>
          <span className="mt-1.5 inline-flex items-center gap-1 text-sm font-semibold text-purple dark:text-gold-300">
            Comenzar el reto
            <span className="transition-transform duration-150 group-hover:translate-x-0.5">
              <ChevronIcon />
            </span>
          </span>
        </span>
      </a>
    </section>
  );
}
